import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AlertTriangle, X } from "lucide-react";
import Modal from "components/common/Modal";

const DeleteWorkspaceModal = ({ show, onClose, onDelete, workspace }) => {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState("");

  const handleClose = () => {
    setConfirmName("");
    onClose();
  };

  const handleDelete = () => {
    if (confirmName !== workspace?.name) return;
    onDelete?.();
    handleClose();
  };

  const isConfirmed = confirmName === workspace?.name;

  return (
    <Modal show={show} onClose={handleClose} maxWidth="480px">
      {/* Header - Red warning gradient */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        {/* Header content */}
        <div className="relative flex items-start justify-between">
          <div>
            {/* Icon */}
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <AlertTriangle size={24} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">
              {t("workspace.deleteWorkspaceModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {t("workspace.shared.cannotBeUndone")}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-7">
        {/* Warning Message */}
        <div
          className="mb-6 flex gap-3 rounded-xl p-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(220, 38, 38, 0.08) 0%, rgba(220, 38, 38, 0.03) 100%)",
            border: "1px solid rgba(220, 38, 38, 0.15)",
          }}
        >
          <AlertTriangle
            size={20}
            className="mt-0.5 flex-shrink-0 text-[#DC2626]"
          />
          <div className="text-[13px] leading-relaxed text-[#6B7280]">
            <p className="mb-1 font-medium text-[#DC2626]">
              {t("workspace.shared.warning")}
            </p>
            <p>
              <Trans
                i18nKey="workspace.deleteWorkspaceModal.warning"
                values={{ name: workspace?.name ?? "" }}
                components={{
                  bold: <span className="font-semibold text-[#111827]" />,
                }}
              />
            </p>
          </div>
        </div>

        {/* Confirmation Field */}
        <div>
          <label className="mb-2 block text-sm font-medium text-[#374151]">
            <Trans
              i18nKey="workspace.shared.typeToConfirm"
              values={{ name: workspace?.name ?? "" }}
              components={{
                bold: <span className="font-semibold text-[#111827]" />,
              }}
            />
          </label>
          <input
            type="text"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={t("workspace.deleteWorkspaceModal.confirmPlaceholder")}
            className="w-full rounded-[10px] border border-[#E5E7EB] px-4 py-3 text-sm text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150"
            style={{
              boxShadow: "none",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#DC2626";
              e.target.style.boxShadow = "0 0 0 3px rgba(220, 38, 38, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#E5E7EB";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 border-t border-[#F3F4F6] px-7 py-5">
        <button
          onClick={handleClose}
          className="rounded-[10px] border border-[#E5E7EB] bg-white px-5 py-2.5 text-sm font-medium text-[#374151] transition-colors duration-150 hover:bg-[#F9FAFB]"
        >
          {t("common.actions.cancel")}
        </button>
        <button
          onClick={handleDelete}
          disabled={!isConfirmed}
          className="rounded-[10px] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-150"
          style={
            isConfirmed
              ? {
                  background: "#DC2626",
                  boxShadow: "0 2px 8px rgba(220, 38, 38, 0.3)",
                  cursor: "pointer",
                }
              : {
                  background: "#F3F4F6",
                  color: "#9CA3AF",
                  cursor: "not-allowed",
                }
          }
        >
          {t("workspace.deleteWorkspaceModal.submit")}
        </button>
      </div>
    </Modal>
  );
};

export default DeleteWorkspaceModal;
